import PropTypes from 'prop-types';
import React, { useCallback, useContext, useState, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { StandardButton } from '@eika/button';
import classNames from 'classnames';
import ahvContext from '../../../ahv-context';
import { BARE_LES_NAR_VALGT } from '../../../constants/general';
import { emptyList } from '../../../constants';
import getLabel from '../labels';
import AktorDetaljer, { getAktorDetailsFields } from './aktor-detaljer';
import { answersToActor, isValidAktor } from './validation';
import CountryContext from '../../../country-context';

function AktorListQuestionType({ question }) {
    const { ekstratekster = {}, config = {}, utfyllingsregel, definisjon } = question;
    const context = useContext(ahvContext);
    const countryContext = useContext(CountryContext);

    const [preFilledAktorList, setPreFilledAktorList] = useState({});

    const aktorDetaljerFields = useMemo(() => getAktorDetailsFields(), []);

    const getShowErrors = useCallback(() => context.getHasSubmitted(), [context]);

    const getInvalidList = useCallback(
        (answer, config) => {
            const keys = Object.keys(aktorDetaljerFields);
            const actor = answersToActor(answer);
            return keys.filter((key) =>
                isValidAktor(key, actor, config, countryContext.getNorwegianPostalCodesOptions) ? false : key
            );
        },
        [aktorDetaljerFields, countryContext.getNorwegianPostalCodesOptions]
    );

    const addPrefilled = (aktorListString) => {
        let aktorList = JSON.parse(aktorListString);
        if (!Array.isArray(aktorList)) {
            aktorList = [aktorList];
        }
        return aktorList.map((aktor, index) => {
            const aktorKey = `${definisjon}-${index}`;
            if (aktor.aktorId) {
                aktor.fnr = aktor.aktorId.value;
                if (utfyllingsregel.regel === BARE_LES_NAR_VALGT && !(aktorKey in preFilledAktorList)) {
                    setPreFilledAktorList((previous) => ({ ...previous, [aktorKey]: aktor }));
                }
            }
            return {
                aktorKey,
                ...aktorDetaljerFields,
                ...aktor,
            };
        });
    };

    const addAktor = useCallback(() => {
        const currentAktorList = context.getValue(question.definisjon);
        const aktorList = [
            ...currentAktorList,
            {
                aktorKey: uuid(),
                ...getAktorDetailsFields(),
            },
        ];
        context.onChange(question.definisjon, { value: aktorList });
    }, [context, question.definisjon]);

    const updateAktor = useCallback(
        (aktorKey) => (data) => {
            let currentAktorList = context.getValue(question.definisjon);
            if (!Array.isArray(currentAktorList)) {
                currentAktorList = [currentAktorList];
            }
            const aktorList = currentAktorList.map((aktor) => {
                if (aktor.aktorKey !== aktorKey) {
                    return aktor;
                }
                const modifiedAktor = { ...aktor, ...data };
                const hasError = getShowErrors() ? getInvalidList(modifiedAktor, config).length > 0 : false;
                return { ...modifiedAktor, error: hasError };
            });

            context.onChange(question.definisjon, { value: aktorList });
        },
        [context, config, getInvalidList, getShowErrors, question.definisjon]
    );

    const removeAktor = useCallback(
        (aktorKey) => () => {
            const currentAktorList = context.getValue(question.definisjon);
            const aktorList = currentAktorList.filter((aktor) => aktor.aktorKey !== aktorKey);
            context.onChange(question.definisjon, { value: aktorList });
        },
        [context, question.definisjon]
    );

    let aktorList = context.getValue(question.definisjon);
    if (typeof aktorList === 'string') {
        aktorList = addPrefilled(aktorList);
        context.onChange(question.definisjon, { value: aktorList });
    }

    const getIsPrefilled = useCallback((aktorKey) => !!preFilledAktorList[aktorKey], [preFilledAktorList]);
    return (
        <>
            {aktorList.map((aktor, i) => (
                <div key={`aktor-${aktor.aktorKey}`} className="skjema-input-field-wrap-margin-bottom">
                    <div className="extra-margin-top">
                        {!config.aktorListHideLabel &&
                            `${getLabel(ekstratekster, 'aktor_card_title')}${
                                !config.aktorListHideIndex ? ` #${i + 1}` : ''
                            }`}
                        {!getIsPrefilled(aktor.aktorKey) && (
                            <button
                                style={{ float: 'right', margin: 'unset', padding: 'unset', height: 'unset' }}
                                className="ion-android-close removeicon remove-btn btn"
                                type="button"
                                title="Slett fra liste"
                                onClick={removeAktor(aktor.aktorKey)}
                                aria-label="Slett fra liste"
                            />
                        )}
                    </div>
                    <AktorDetaljer
                        context={context}
                        question={aktor}
                        onChange={updateAktor(aktor.aktorKey)}
                        errors={getShowErrors() ? getInvalidList(aktor, config) : emptyList}
                        tekster={question.ekstratekster}
                        index={i}
                        prefilledAktor={preFilledAktorList[aktor.aktorKey] || false}
                        config={config}
                    />
                </div>
            ))}
            {(!config.aktorListMaxLength || aktorList.length < config.aktorListMaxLength) && (
                <StandardButton
                    onClick={addAktor}
                    className={classNames({ 'extra-margin-top': aktorList && aktorList.length > 0 })}
                >
                    {getLabel(ekstratekster, 'aktor_add')}
                </StandardButton>
            )}
        </>
    );
}

AktorListQuestionType.propTypes = {
    question: PropTypes.shape({
        definisjon: PropTypes.string.isRequired,
        utfyllingsregel: PropTypes.shape({
            regel: PropTypes.string.isRequired,
        }).isRequired,
        ekstratekster: PropTypes.shape({
            aktor_card_title: PropTypes.string,
            aktor_navn: PropTypes.string,
            aktor_fnr: PropTypes.string,
            aktor_fodeland: PropTypes.string,
            aktor_statsborgerskap: PropTypes.string,
            aktor_sekundart_statsborgerskap: PropTypes.string,
            aktor_adresse: PropTypes.string,
            aktor_postnummer: PropTypes.string,
            aktor_poststed: PropTypes.string,
            aktor_bostedsland: PropTypes.string,
            aktor_add: PropTypes.string,
        }),
        config: PropTypes.shape({
            addressInformationOptional: PropTypes.bool,
            aktorListHideLabel: PropTypes.bool,
            aktorListHideIndex: PropTypes.bool,
            aktorListMaxLength: PropTypes.number,
        }),
    }).isRequired,
};

export default AktorListQuestionType;
